import type { ScaleLinear, ScaleSymLog } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import type { ChartUnit } from '../../core/types/chart-unit.js';
import { MinMaxConfig } from '../../core/types/min-max-config.js';
import { MinMax } from '../../core/types/min-max.js';
import { AxisScaleType } from '../../core/types/scales.js';
import { buildScale } from '../../core/utils/core-scale.js';
import { TimeseriesChartYAxisProps } from '../slots/YAxis.js';
import { YAxisScale, YAxisScales } from '../types/state.js';
import { TimeseriesChartYAxisConfig } from '../types/timeseries-config.js';
import {
  ChartVariant,
  WeakMapSeries,
  WeakMapDatapoints,
  TimeseriesPerVariant,
  type ScaleMetadata,
} from '../types/timeseries.js';
import { getYAxisValueDomainsByUnit } from './yScaleDomain.js';

/**
 * Finds the y-scale based on the series unit.
 *
 * @param seriesUnit -
 * @param yScales -
 * @returns
 */
export function getYScaleByUnit(
  seriesUnit: ChartUnit,
  yScales: YAxisScales,
): ScaleLinear<number, number> | ScaleSymLog<number, number> {
  if (seriesUnit === yScales.left?.unit) {
    return (yScales.left as YAxisScale).scale;
  }
  const axisScale = (yScales.right || yScales.left) as YAxisScale;
  return axisScale.scale;
}

/**
 * Finds the y-scale type based on the series unit.
 *
 * @param seriesUnit -
 * @param yScales -
 * @param axisScaleType -
 * @returns
 */
export function getYScaleTypeByUnit(
  seriesUnit: ChartUnit,
  yScales: YAxisScales,
  axisScaleType: TimeseriesChartYAxisConfig,
): AxisScaleType {
  if (seriesUnit === yScales.left?.unit) {
    return axisScaleType.left?.scale ?? 'linear';
  }

  return (axisScaleType.right?.scale || axisScaleType.left?.scale) ?? 'linear';
}

const getConfigMinMax = (
  yAxisConfig: TimeseriesChartYAxisProps | undefined,
): Partial<MinMaxConfig> | undefined => {
  if (!yAxisConfig) {
    return undefined;
  }

  return {
    min: yAxisConfig.min,
    max: yAxisConfig.max,
  };
};

const isMissingData = (dataBoundaries: ScaleMetadata | undefined) =>
  isUndefined(dataBoundaries) ||
  isUndefined(dataBoundaries.min) ||
  isUndefined(dataBoundaries.max);

/**
 * Method to build a scale based on data-boundaries and configuration given by the
 * user, setting a nice scale for those values not configured.
 *
 * @param height - height of the chart
 * @param dataBoundaries -
 * @param configMinMax -
 * @param scaleType -
 */
const buildYScale = (
  height: number,
  dataBoundaries: Required<ScaleMetadata>,
  configMinMax: Partial<MinMaxConfig> | undefined,
  scaleType: AxisScaleType = 'linear',
) => {
  return {
    scale: buildScale(scaleType, [height, 0], dataBoundaries, configMinMax),
    unit: dataBoundaries.unit,
  };
};

/**
 * Utility method that converts ChartUnit, MinMax Map into an Array
 * holding \{ min, max, unit \}
 *
 * @param mapBoundariesUnit -
 */
const parseMapToArray = (
  mapBoundariesUnit: Map<ChartUnit, MinMax>,
): ScaleMetadata[] => {
  const response: ScaleMetadata[] = [];
  for (const [unit, { min, max }] of mapBoundariesUnit) {
    response.push({ unit, min, max });
  }

  return response;
};

const getDataBoundaries = (
  timeseries: TimeseriesPerVariant,
  seriesMap: WeakMapSeries,
  datapointsMap: WeakMapDatapoints,
): ScaleMetadata[] => {
  const leftMetadata: ScaleMetadata = {};
  const rightMetadata: ScaleMetadata = {};

  const mapBoundariesUnit = timeseries.shapePriority.reduce(
    (boundariesByUnit, variant) => {
      const shapeDomains = getYAxisValueDomainsByUnit(
        timeseries[variant as ChartVariant],
        variant,
        leftMetadata,
        rightMetadata,
        seriesMap,
        datapointsMap,
      );

      shapeDomains.forEach(({ min, max, unit }) => {
        if (boundariesByUnit.has(unit)) {
          const current = boundariesByUnit.get(unit) as MinMax;
          const boundaries = {
            min: min ? Math.min(current.min, min) : current.min,
            max: max ? Math.max(current.max, max) : current.max,
          };
          boundariesByUnit.set(unit, boundaries);
        } else {
          boundariesByUnit.set(unit, { min, max } as MinMax);
        }
      });

      return boundariesByUnit;
    },
    new Map<ChartUnit, MinMax>(),
  );

  return parseMapToArray(mapBoundariesUnit);
};

/**
 * Based on configuration assign boundaries to left and/or right axis
 *
 * @param leftConfigMinMax -
 * @param rightConfigMinMax -
 * @param firstUnitBoundaries -
 * @param secondUnitBoundaries -
 */
export const assignBoundariesToYAxes = (
  leftConfigMinMax: Partial<MinMaxConfig> | undefined,
  rightConfigMinMax: Partial<MinMaxConfig> | undefined,
  firstUnitBoundaries: ScaleMetadata | undefined,
  secondUnitBoundaries: ScaleMetadata | undefined,
) => {
  const hasRightAxis = !isUndefined(rightConfigMinMax);
  const hasSecondDomain = !isUndefined(secondUnitBoundaries);

  if (!hasSecondDomain) {
    return hasRightAxis
      ? { rightUnitBoundaries: firstUnitBoundaries }
      : { leftUnitBoundaries: firstUnitBoundaries };
  }

  return {
    leftUnitBoundaries: firstUnitBoundaries,
    rightUnitBoundaries: secondUnitBoundaries,
  };
};

const shouldReturnUserConfig = (
  mainConfig: Partial<MinMaxConfig> | undefined,
): boolean => !isUndefined(mainConfig);

function shouldBuildScale(
  unitBoundaries: ScaleMetadata,
  configMinMax: Partial<MinMaxConfig> | undefined,
) {
  return !isMissingData(unitBoundaries) && shouldReturnUserConfig(configMinMax);
}

function shouldDisplayAxis(
  unitBoundaries: ScaleMetadata,
  defaultDisplay: boolean,
  configMinMax: Partial<MinMaxConfig> | undefined,
) {
  return (
    unitBoundaries &&
    (shouldBuildScale(unitBoundaries, configMinMax) || defaultDisplay)
  );
}

/**
 * Builds 2 scales if needed and assign them to left and/or right axis based in
 * configuration given by the user.
 *
 * @param timeseries -
 * @param config -
 * @param height -
 * @param seriesMap -
 * @param datapointsMap -
 */
export const buildYScales = (
  timeseries: TimeseriesPerVariant,
  config: TimeseriesChartYAxisConfig,
  height: number,
  seriesMap: WeakMapSeries,
  datapointsMap: WeakMapDatapoints,
): YAxisScales => {
  const leftScaleType = config.left?.scale;
  const rightScaleType = config.right?.scale;

  const leftConfigMinMax = getConfigMinMax(config.left);
  const rightConfigMinMax = getConfigMinMax(config.right);

  const [firstUnitBoundaries, secondUnitBoundaries] = getDataBoundaries(
    timeseries,
    seriesMap,
    datapointsMap,
  );

  const { leftUnitBoundaries, rightUnitBoundaries } = assignBoundariesToYAxes(
    leftConfigMinMax,
    rightConfigMinMax,
    firstUnitBoundaries,
    secondUnitBoundaries,
  );

  const displayBothAxisByDefault = !leftConfigMinMax && !rightConfigMinMax;

  const displayLeftAxis = shouldDisplayAxis(
    leftUnitBoundaries as ScaleMetadata,
    displayBothAxisByDefault,
    leftConfigMinMax,
  );
  const displayRightAxis = shouldDisplayAxis(
    rightUnitBoundaries as ScaleMetadata,
    displayBothAxisByDefault,
    rightConfigMinMax,
  );

  const left = displayLeftAxis
    ? buildYScale(
        height,
        leftUnitBoundaries as Required<ScaleMetadata>,
        leftConfigMinMax,
        leftScaleType,
      )
    : undefined;

  const right = displayRightAxis
    ? buildYScale(
        height,
        rightUnitBoundaries as Required<ScaleMetadata>,
        rightConfigMinMax,
        rightScaleType,
      )
    : undefined;

  return {
    left,
    right,
  } as YAxisScales;
};
